import {
  Radio,
  Sparkles,
  FileSpreadsheet,
  Users,
  BarChart3,
  Activity,
  KeyRound,
  FlaskConical,
  type LucideIcon,
} from "lucide-react";

/**
 * Single source of truth for the admin section navigation.
 *
 * Adding a new admin section is a one-line change here (plus the
 * matching <case> branch in pages/admin.tsx for in-tab sections, or
 * a new page route in App.tsx for full-page sections like usage/ratings).
 *
 * `href` is only used when the section lives at a non-standard URL.
 * Otherwise it's derived as `/admin/${slug}`.
 */

export type AdminSectionGroup =
  | "Content"
  | "Data Pipelines"
  | "Integrations"
  | "People"
  | "System";

export interface AdminSection {
  slug: string;
  label: string;
  description?: string;
  icon: LucideIcon;
  group: AdminSectionGroup;
  /** Override the URL — used for sections that have their own full-page route. */
  href?: string;
}

export const ADMIN_SECTIONS: AdminSection[] = [
  // Content
  {
    slug: "stations",
    label: "Stations",
    description: "Manage partner stations and global knowledge base",
    icon: Radio,
    group: "Content",
  },
  {
    slug: "vbr",
    label: "VBR Engine",
    description: "Prompts, models, and parameters for VBR generation",
    icon: Sparkles,
    group: "Content",
  },

  // Data Pipelines
  {
    slug: "npr",
    label: "NPR Profile",
    description: "Pipeline B — NPR profile fact processing",
    icon: FileSpreadsheet,
    group: "Data Pipelines",
  },
  {
    slug: "tapscan",
    label: "Nielsen Tapscan",
    description: "Pipeline B — Nielsen Tapscan fact processing",
    icon: FileSpreadsheet,
    group: "Data Pipelines",
  },
  {
    slug: "generic",
    label: "Generic Data",
    description: "Pipeline B — generic spreadsheet fact processing",
    icon: FileSpreadsheet,
    group: "Data Pipelines",
  },

  // Integrations
  {
    slug: "copy-api",
    label: "Copy API",
    description: "Public copy-generation API: prompt, model, and key management",
    icon: KeyRound,
    group: "Integrations",
  },
  {
    slug: "copy-api-test",
    label: "Copy API Test",
    description: "Run the Copy API prompt with sample inputs and review the three variants",
    icon: FlaskConical,
    group: "Integrations",
  },

  // People
  {
    slug: "users",
    label: "Users",
    description: "User accounts, roles, and station assignments",
    icon: Users,
    group: "People",
  },
  {
    slug: "usage",
    label: "Usage Analytics",
    description: "Copy Engine and VBR usage over time",
    icon: BarChart3,
    group: "People",
    href: "/admin/usage",
  },

  // System
  {
    slug: "api-status",
    label: "API Status",
    description: "Live service health and platform info",
    icon: Activity,
    group: "System",
  },
  {
    slug: "experience",
    label: "Experience",
    description: "Roll out the new experience: disable the classic view for all users",
    icon: Sparkles,
    group: "System",
  },
];

const GROUP_ORDER: AdminSectionGroup[] = [
  "Content",
  "Data Pipelines",
  "Integrations",
  "People",
  "System",
];

/** Sections grouped and ordered for sidebar rendering. */
export function getGroupedSections(): Array<{
  group: AdminSectionGroup;
  sections: AdminSection[];
}> {
  return GROUP_ORDER.map(group => ({
    group,
    sections: ADMIN_SECTIONS.filter(s => s.group === group),
  })).filter(g => g.sections.length > 0);
}

/** Resolve a section's URL (custom href wins, else /admin/<slug>). */
export function sectionHref(section: AdminSection): string {
  return section.href ?? `/admin/${section.slug}`;
}

/** Default landing section for /admin without a slug. */
export const DEFAULT_ADMIN_SECTION: AdminSection = ADMIN_SECTIONS[0];

/** Look up a section by URL slug. Returns undefined for unknown slugs. */
export function findSectionBySlug(slug: string): AdminSection | undefined {
  return ADMIN_SECTIONS.find(s => s.slug === slug);
}
